import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../Utils/Axios";

function CompanyLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [companyLanguage, setCompanyLanguage] = useState('en');

  useEffect(() => {
    // Get company language from localStorage
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);

    // Listen for language updates
    const handleStorageChange = () => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      setCompanyLanguage(updatedLang);
    };

    window.addEventListener('companyDataUpdated', handleStorageChange);

    // Check periodically
    const interval = setInterval(() => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      if (updatedLang !== companyLanguage) {
        setCompanyLanguage(updatedLang);
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Frontend validation
    const isUrdu = companyLanguage === 'ur';
    if (!formData.email || !formData.password) {
      toast.error(isUrdu ? "براہ کرم ای میل اور پاس ورڈ دونوں درج کریں" : "Please enter both email and password");
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error(isUrdu ? "براہ کرم درست ای میل ایڈریس درج کریں" : "Please enter a valid email address");
      return;
    }

    // Password length validation
    if (formData.password.length < 6) {
      toast.error(isUrdu ? "پاس ورڈ کم از کم 6 حروف کا ہونا چاہیے" : "Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log("Attempting login");
      }

      const response = await api.post("/auth/company/login", {
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      });

      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log("Login response status:", response.status);
      }

      // Strict validation - only proceed if we have token AND company data
      if (response.data && response.data.token && response.data.company) {
        // Verify token is not empty
        if (!response.data.token || response.data.token.length < 10) {
          throw new Error("Invalid token received from server");
        }

        // Store token and company data immediately
        localStorage.setItem("companyToken", response.data.token);
        localStorage.setItem("companyData", JSON.stringify(response.data.company));

        // Store company language for global access
        const companyLang = response.data.company?.websiteLanguage || response.data.company?.language || 'en';
        localStorage.setItem("companyLanguage", companyLang);

        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log("Token saved to localStorage");
          console.log("Company language:", companyLang);
          console.log("Navigating to dashboard...");
        }

        const isUrdu = companyLanguage === 'ur';
        toast.success(isUrdu ? "لاگ ان کامیاب! ڈیش بورڈ پر منتقل ہو رہے ہیں..." : "Login successful! Redirecting to dashboard...");

        // Navigate based on dashboardType only (single source of truth from API)
        const company = response.data.company;
        const raw = company?.dashboardType || 'main';
        const isPart2 = raw === 'part2' || raw === 'dashboard-part2' || raw === 'part-2' || raw === 'ur' || raw === 'urdu';
        const isEn = raw === 'en' || raw === 'english' || raw === 'dashboard-en';

        if (isPart2) {
          navigate("/company/dashboard-part2", { replace: true });
        } else if (isEn) {
          navigate("/company/dashboard-en", { replace: true });
        } else {
          navigate("/company/dashboard", { replace: true });
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error("Invalid response structure:", response.data);
        }
        throw new Error("Invalid response from server - missing token or company data");
      }
    } catch (error) {
      // Only log errors in development
      if (process.env.NODE_ENV === 'development') {
        console.error("Login error:", error);
        console.error("Error message:", error.message);
      }

      // Clear form on error
      setFormData({ email: "", password: "" });

      const isUrdu = companyLanguage === 'ur';

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const errorMessage = error.response.data?.message || error.response.data?.error || "Login failed. Please check your credentials.";

        // Special handling for desktop passkey expiry (online / offline)
        const isPasskeyExpired =
          typeof errorMessage === 'string' &&
          (errorMessage.toLowerCase().includes('offline pass key expired') ||
            errorMessage.toLowerCase().includes('online pass key expired'));

        if (isPasskeyExpired) {
          // Clear stored company token so user must login again after new keys
          localStorage.removeItem("companyToken");

          // In Electron desktop app: reset local passkeys and reopen passkey window
          if (typeof window !== 'undefined' && window.isElectron && window.desktopPasskey && typeof window.desktopPasskey.reset === 'function') {
            try {
              await window.desktopPasskey.reset();
            } catch (e) {
              // ignore reset errors on web
            }
          }

          toast.error(
            isUrdu
              ? "آپ کی پاس کی (Passkey) کی تاریخ ختم ہو گئی ہے۔ براہ کرم ایڈمن سے نئی key لیں اور دوبارہ درج کریں۔"
              : "Your desktop pass key has expired. Please contact admin for a new key and enter it again."
          );
        } else if (status === 401) {
          toast.error(isUrdu ? "غلط ای میل یا پاس ورڈ۔ براہ کرم دوبارہ کوشش کریں۔" : "Invalid email or password. Please try again.");
        } else if (status === 400) {
          toast.error(errorMessage);
        } else if (status === 500) {
          toast.error(isUrdu ? "سرور کی خرابی۔ براہ کرم بعد میں کوشش کریں۔" : "Server error. Please try again later.");
        } else {
          toast.error(errorMessage);
        }
        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.error("Error status:", status);
          console.error("Error message:", errorMessage);
        }
      } else if (error.request) {
        // Request made but no response
        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.error("No response received");
        }

        // Check if it's a network error
        if (error.code === 'ECONNABORTED') {
          toast.error(isUrdu ? "درخواست کا وقت ختم ہو گیا۔ براہ کرم دوبارہ کوشش کریں۔" : "Request timeout. Please try again.");
        } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
          toast.error(isUrdu ? "نیٹ ورک کی خرابی۔ براہ کرم اپنا انٹرنیٹ کنکشن چیک کریں۔" : "Network error. Please check your internet connection.");
        } else {
          toast.error(isUrdu ? "سرور جواب نہیں دے رہا۔ براہ کرم چیک کریں کہ بیک اینڈ چل رہا ہے۔" : "Server not responding. Please check if backend is running.");
        }
      } else {
        // Error in request setup
        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.error("Request setup error:", error.message);
        }
        toast.error(isUrdu ? `لاگ ان ناکام ہوا: ${error.message}` : `Login failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isUrdu = companyLanguage === 'ur';

  return (
    <>
      <Helmet><title>{isUrdu ? 'کمپنی لاگ ان' : 'Company Login'} - Galla Mandi</title></Helmet>

      <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
        {/* HD Fruit Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/bg11.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {/* Dark Overlay for Text Readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60"></div>
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-brand-header/10 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-brand-header/10 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        </div>

        {/* Login Card */}
        <div className="relative z-10 w-full max-w-md" dir={isUrdu ? 'rtl' : 'ltr'}>
          <div className="bg-brand-card/95 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border-2 border-brand-header/30 animate-fade-in">
            {/* Logo/Header */}
            <div className={`text-center mb-8 animate-slide-down ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              <Link to="/" className="inline-block mb-4">
                <h1 className="text-4xl font-bold text-brand-header">Galla Mandi</h1>
              </Link>
              <h2 className={`text-3xl font-bold text-brand-header mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {isUrdu ? 'کمپنی لاگ ان' : 'Company Login'}
              </h2>
              <p className={`text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {isUrdu ? 'اپنے ڈیش بورڈ تک رسائی کے لیے سائن ان کریں' : 'Sign in to access your dashboard'}
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in-up">
              {/* Email Field */}
              <div className="space-y-2">
                <label className={`block text-brand-header font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'ای میل ایڈریس' : 'Email Address'}
                </label>
                <div className="relative">
                  <div className={`absolute inset-y-0 ${isUrdu ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    dir="ltr"
                    className={`w-full ${isUrdu ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all`}
                    placeholder={isUrdu ? "company@example.com" : "company@example.com"}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label className={`block text-brand-header font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {isUrdu ? 'پاس ورڈ' : 'Password'}
                </label>
                <div className="relative">
                  <div className={`absolute inset-y-0 ${isUrdu ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    dir="ltr"
                    className={`w-full ${isUrdu ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all`}
                    placeholder={isUrdu ? "اپنا پاس ورڈ درج کریں" : "Enter your password"}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full bg-brand-button hover:bg-white/90 text-brand-header font-bold py-3 px-6 rounded-lg transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg ${isUrdu ? 'font-urdu' : ''}`}
                style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className={`animate-spin h-5 w-5 text-brand-header ${isUrdu ? 'ml-3' : 'mr-3'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isUrdu ? 'لاگ ان ہو رہا ہے...' : 'Signing in...'}
                  </span>
                ) : (
                  isUrdu ? 'سائن ان کریں' : 'Sign In'
                )}
              </button>
            </form>

            {/* Back to Home */}
            <div className="text-center mt-6">
              <Link to="/" className={`text-brand-header/80 hover:text-brand-header transition flex items-center justify-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                <svg className={`w-5 h-5 ${isUrdu ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isUrdu ? "M14 5l7 7m0 0l-7 7m7-7H3" : "M10 19l-7-7m0 0l7-7m-7 7h18"} />
                </svg>
                {isUrdu ? 'ہوم پر واپس' : 'Back to Home'}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Add custom animations */}
      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.6s ease-out; }
        @keyframes slide-down { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-down { animation: slide-down 0.6s ease-out; }
        @keyframes fade-in-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in-up { animation: fade-in-up 0.8s ease-out 0.2s both; }
      `}</style>
    </>
  );
}

export default CompanyLogin;

